import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {contractTypeToLabel, paymentMethodToLabel} from '@/features/users/utils'
import {StyledUppercaseTextInput} from './style'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {InputText} from '@/components/commons/input-text/InputText'
import {UserContractualDataFormValidationSchema} from '@/features/users/types'
import {InputPrice} from '@/components/commons/input-price/InputPrice'

export const UserContractualDataForm = () => {
    const {t} = useTranslation()

    // methods
    const form = useFormContext<UserContractualDataFormValidationSchema>()

    // form observer
    const contractType = useWatch({control: form.control, name: 'contractType'})
    const raExempt = useWatch({control: form.control, name: 'raExempt'})

    // side effects
    const onChangeContractTypeSideEffects = () => {
        form.setValue('vatNumber', null, {shouldDirty: true})
        form.setValue('weeklyHours', null, {shouldDirty: true})
        form.setValue('amount', null, {shouldDirty: true})
        form.setValue('raExempt', false, {shouldDirty: true})
        form.setValue('privacyB', false, {shouldDirty: true})
        form.setValue('privacyC', false, {shouldDirty: true})
    }

    return (
        <Grid columns={6} fullWidth gap={4}>
            <GridItem spanColumn={6}>
                <Controller
                    control={form.control}
                    name="contractType"
                    render={({field}) => (
                        <InputSelect
                            label={t('users:contract_type')}
                            optionsMap={contractTypeToLabel}
                            sameWidth
                            value={field.value ?? undefined}
                            onChange={value => {
                                field.onChange(value)
                                onChangeContractTypeSideEffects()
                            }}
                            errorMessage={form.formState.errors.contractType?.message}
                        />
                    )}
                />
            </GridItem>

            {contractType && contractType != 'employee' && (
                <>
                    <GridItem spanColumn={2}>
                        <StyledUppercaseTextInput
                            type="text"
                            label={t('commons:vat_number')}
                            placeholder={'12345678901'}
                            errorMessage={form.formState.errors.vatNumber?.message}
                            {...form.register('vatNumber')}
                        />
                    </GridItem>
                    <GridItem spanColumn={2}>
                        <InputText
                            type="number"
                            label={t('commons:hours_per_week')}
                            min={1}
                            max={40}
                            placeholder="40"
                            isOptional
                            errorMessage={form.formState.errors.weeklyHours?.message}
                            {...form.register('weeklyHours', {
                                setValueAs: v => (v == '' || v == null ? undefined : parseInt(v, 10))
                            })}
                        />
                    </GridItem>
                    {(contractType == 'flat_rate' || contractType == 'consultant') && (
                        <GridItem spanColumn={2}>
                            <Controller
                                control={form.control}
                                name="amount"
                                render={({field}) => (
                                    <InputPrice
                                        getInputRef={field.ref}
                                        label={
                                            contractType == 'consultant'
                                                ? t('users:single_amount')
                                                : t('users:monthly_amount')
                                        }
                                        placeholder={'0,00'}
                                        name="amount"
                                        value={field.value}
                                        onValueChange={values => {
                                            field.onChange(values.value)
                                        }}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        allowNegative={false}
                                        isOptional
                                    />
                                )}
                            />
                        </GridItem>
                    )}
                    <GridItem startColumn={1} endColumn={7}>
                        <Checkbox
                            label={t('users:ra_exempt')}
                            id="raExempt"
                            checked={!!raExempt}
                            {...form.register('raExempt')}
                        />
                    </GridItem>
                </>
            )}

            {contractType && (
                <>
                    <GridItem startColumn={1} endColumn={4}>
                        <Controller
                            control={form.control}
                            name="paymentMethod"
                            render={({field}) => (
                                <InputSelect
                                    label={t('commons:payment_method')}
                                    optionsMap={paymentMethodToLabel}
                                    sameWidth
                                    value={field.value ?? undefined}
                                    onChange={field.onChange}
                                    errorMessage={form.formState.errors.paymentMethod?.message}
                                />
                            )}
                        />
                    </GridItem>

                    <GridItem startColumn={4} endColumn={7}>
                        <StyledUppercaseTextInput
                            type="text"
                            label={t('commons:iban')}
                            placeholder={'IT00X1234567890123456789012'}
                            errorMessage={form.formState.errors.iban?.message}
                            {...form.register('iban')}
                        />
                    </GridItem>
                </>
            )}
        </Grid>
    )
}
